import { useOmniAds } from '@libs/utils/hooks/OmniAds';
import { useRedline } from '@libs/redline';

interface UseBilletTrackingProps {
  agreement: any;
  installment: any;
  closedAgreement?: any;
}

const useBilletTracking = ({ agreement, installment, closedAgreement }: UseBilletTrackingProps) => {
  const { track: trackRedline } = useRedline();
  const { omniTracker } = useOmniAds();

  interface MountRedlinePayloadProps {
    outcomeType: 'COPY' | 'SHARE' | 'DOWNLOAD';
    paymentType: 'BILLET' | 'PIX';
    code?: string;
  }

  function mountRedlinePayload({ paymentType, code, outcomeType }: MountRedlinePayloadProps) {
    return {
      paymentType,
      code,
      agreementId: agreement?.id,
      outcomeType,
      installmentId: installment?.installmentCode,
    } as const;
  }

  function trackCodeCopy(codeToCopy: string) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code: codeToCopy, outcomeType: 'COPY' });
    trackRedline.renegkit.paymentButtonClicked(redlineData);
  }

  function trackCodeRetrieved(codeToCopy: string) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code: codeToCopy, outcomeType: 'COPY' });
    trackRedline.renegkit.paymentCodeRetrieved(redlineData);

    const eventName = 'agreements.PaymentCodeCopied.v1'; // TODO Renegkit Validate omni
    const payloadOmni = {
      code: codeToCopy,
      codeType: 'BILLET',
      agreementId: agreement?.id || closedAgreement?.agreement?.id,
      debtId: agreement?.debt?.id || closedAgreement?.debt?.id,
      installmentId: installment?.id,
      installment: installment?.installmentNumber || installment?.parcela,
      installmentValue: installment?.installmentValue || installment?.valorParcela,
      type: 'BILLET',
    };

    omniTracker.all({
      google: {
        target: 'gALL',
        conversionId: 'AW-392945561/6rcWCK2ZpoIbEJm_r7sB',
        eventName: eventName,
        payload: {
          ...payloadOmni,
        },
      },
      meta: {
        eventName: 'Custom',
        payload: {
          eventName: eventName,
          ...payloadOmni,
        },
      },
    });
  }

  function trackRetrievalFailed(codeToCopy: string, error: Error) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code: codeToCopy, outcomeType: 'COPY' });

    trackRedline.renegkit.paymentCodeRetrievalFailed({
      ...redlineData,
      error: error.message,
    });
  }

  function trackBilletDownload(code: string) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code, outcomeType: 'DOWNLOAD' });
    trackRedline.renegkit.paymentButtonClicked(redlineData);
  }

  function trackBilletFileRetrieved(code: string) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code, outcomeType: 'DOWNLOAD' });
    trackRedline.renegkit.paymentCodeRetrieved(redlineData);
  }

  function trackDownloadFailed(code: string, error: Error) {
    const redlineData = mountRedlinePayload({ paymentType: 'BILLET', code, outcomeType: 'DOWNLOAD' });

    trackRedline.renegkit.paymentCodeRetrievalFailed({
      ...redlineData,
      error: error.message,
    });
  }

  return {
    trackCodeCopy,
    trackCodeRetrieved,
    trackRetrievalFailed,
    mountRedlinePayload,
    trackBilletDownload,
    trackBilletFileRetrieved,
    trackDownloadFailed,
  };
};

export default useBilletTracking;
